import { auth, firestore, collection, doc, setDoc, getDoc, deleteDoc, Timestamp } from '../config/firebase';
import { ServiceResponse } from './authService';

export interface InstallToken {
  userId: string;
  token: string;
  createdAt: Date;
  expiresAt: Date;
  used: boolean;
}

const INSTALL_TOKENS_COLLECTION = 'installTokens';
const TOKEN_TTL_MINUTES = 15;

const generateSecureToken = async (): Promise<string> => {
  const Crypto = require('expo-crypto');
  const randomBytes = await Crypto.getRandomBytesAsync(32);
  return Array.from(randomBytes)
    .map((b: number) => b.toString(16).padStart(2, '0'))
    .join('');
};
export const createInstallToken = async (): Promise<ServiceResponse<string>> => {
  try {
    const user = auth?.currentUser;
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Generate secure token
    const token = await generateSecureToken();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + TOKEN_TTL_MINUTES * 60 * 1000);

    // Store in Firestore
    const tokenDoc: InstallToken = {
      userId: user.uid,
      token,
      createdAt: now,
      expiresAt,
      used: false,
    };

    const tokenRef = doc(firestore, INSTALL_TOKENS_COLLECTION, token);
    await setDoc(tokenRef, {
      ...tokenDoc,
      createdAt: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
    });

    return { success: true, data: token };
  } catch (error: any) {
    console.error('[InstallTokenService] Failed to create token:', error);
    return { success: false, error: error.message || 'Failed to create install token' };
  }
};
export const verifyInstallToken = async (token: string): Promise<ServiceResponse<string>> => {
  try {
    if (!token) {
      return { success: false, error: 'Token is required' };
    }

    // Get token from Firestore
    const tokenRef = doc(firestore, INSTALL_TOKENS_COLLECTION, token);
    const tokenSnap = await getDoc(tokenRef);

    if (!tokenSnap.exists()) {
      return { success: false, error: 'Invalid or expired token' };
    }

    const tokenData = tokenSnap.data() as any;

    // Check if already used
    if (tokenData.used) {
      return { success: false, error: 'Token has already been used' };
    }

    // Check expiration
    const expiresAt = tokenData.expiresAt?.toDate?.() || new Date(tokenData.expiresAt);
    if (expiresAt < new Date()) {
      // Delete expired token
      await deleteDoc(tokenRef);
      return { success: false, error: 'Token has expired' };
    }

    // Delete token immediately (one-time use)
    await deleteDoc(tokenRef);
    return { success: true, data: tokenData.userId };
  } catch (error: any) {
    console.error('[InstallTokenService] Failed to verify token:', error);
    return { success: false, error: error.message || 'Failed to verify token' };
  }
};

